import { useUser } from '@plato/app-core/hooks/use-user';
import { PlatoBust } from '@plato/app-shared/components/plato-bust';
import {
    PromptInput,
    PromptInputSubmit,
    PromptInputTextarea,
} from '@plato/app-shared/components/prompt-input';
import { useAskPlatoLauncher } from '@plato/app-shared/hooks/use-ask-plato-launcher';
import { ToolbarIconButton } from '@plato/ui/candidates/toolbar-icon-button';
import { Button } from '@plato/ui/primitives/button';
import { InputGroupAddon } from '@plato/ui/primitives/input-group';
import { Typography } from '@plato/ui/primitives/typography';
import { cn } from '@plato/ui/util/classes';
import { useBreakpoint } from '@plato/ui/util/use-breakpoint';
import { BarChart3Icon, CalendarCheckIcon, MicIcon, TrendingDownIcon } from 'lucide-react';
import { useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../internal/use-analytics.ts';

// Touch bumps every control to the 44px target, as in the panel's composer.
const touchTarget = '[@media(hover:none)]:h-11 [@media(hover:none)]:min-w-11';

/**
 * Ask Plato on the home dashboard: a greeting, Plato's invitation and one
 * large composer with starter questions inside it. Questions open the chat
 * panel in a fresh conversation, so nothing here holds a chat of its own.
 *
 * The visit-prep starter opens the chat with the question pre-filled and the
 * cursor where the customer name goes. German puts the name mid-sentence
 * ("…Termin bei [Kunde] vor"), so the copy marks the spot with `{{caret}}`.
 */
export function AskPlatoHome(): ReactElement {
    const { t } = useTranslation('ask-plato');
    const { firstName } = useUser();
    const { askPlato, draftAskPlato, recordVoice } = useAskPlatoLauncher();
    const sendEvent = useAnalytics();
    // The examples in the long placeholder don't fit a phone.
    const isDesktop = useBreakpoint('md');
    const [draft, setDraft] = useState('');

    const ask = (question: string): void => {
        if (question.trim().length === 0) {
            return;
        }
        askPlato(question, 'home_card');
        setDraft('');
    };

    const hour = new Date().getHours();
    const greeting =
        hour < 11
            ? t('home__greeting_morning', { name: firstName })
            : hour < 18
              ? t('home__greeting_day', { name: firstName })
              : t('home__greeting_evening', { name: firstName });

    return (
        <section className="mx-auto flex w-full max-w-3xl flex-col gap-container py-relaxed">
            <div className="flex flex-col items-center gap-tight text-center">
                <Typography variant="display">{greeting}</Typography>
                {/* Plato says the line, as a chat message: the avatar sits on its words
                    and bottom-aligns with the last line when the bubble wraps. */}
                <div className="mt-default flex items-end gap-cozy text-left">
                    <span className="size-11 shrink-0">
                        <PlatoBust speaking={false} blink follow />
                    </span>
                    <Typography
                        variant="body"
                        className="rounded-2xl rounded-bl-tight bg-container-inset px-relaxed py-default text-content"
                    >
                        {t('home__plato_line')}
                    </Typography>
                </div>
            </div>

            {/* The card is the surface, so the input group drops its own border, radius
                and focus ring; focus darkens the card's hairline instead. */}
            <div
                className={cn(
                    '@container overflow-clip rounded-3xl border border-line-weakest bg-container shadow-lg',
                    'transition-colors duration-state',
                    'has-[[data-slot=input-group-control]:focus-visible]:border-line',
                )}
            >
                <PromptInput
                    className={cn(
                        'min-h-14 rounded-none border-0 bg-container pr-cozy pl-default shadow-none',
                        'has-[[data-slot=input-group-control]:focus-visible]:ring-0',
                    )}
                    onSubmit={(event) => {
                        event.preventDefault();
                        ask(draft);
                    }}
                >
                    {/* Centres one line in the row; the padding sits here, so once the text
                        grows to five lines only the text scrolls. */}
                    <div className="flex min-w-0 flex-1 items-center self-stretch py-default">
                        <PromptInputTextarea
                            rows={1}
                            name="prompt"
                            aria-label={t('home__composer_label')}
                            value={draft}
                            onChange={(event) => {
                                setDraft(event.target.value);
                            }}
                            placeholder={isDesktop ? t('placeholder') : t('home__placeholder_short')}
                            className="max-h-[5lh] min-h-0 overflow-y-auto py-0 text-ui-lg md:text-ui-base"
                        />
                    </div>
                    <InputGroupAddon align="inline-end">
                        <ToolbarIconButton
                            label={t('home__voice_input')}
                            className={touchTarget}
                            onClick={() => {
                                recordVoice((text) => {
                                    setDraft((current) =>
                                        current.trim().length > 0 ? `${current} ${text}` : text,
                                    );
                                });
                            }}
                        >
                            <MicIcon />
                        </ToolbarIconButton>
                        <PromptInputSubmit
                            className={cn('rounded-full', touchTarget)}
                            disabled={draft.trim().length === 0}
                            label={t('send')}
                        />
                    </InputGroupAddon>
                </PromptInput>

                {/* Switches on the card's width, not the viewport, so a narrow card beside the
                    sidebar gets rows too: full-width rows below 44rem, from there three equal
                    cells split by hairlines (the 1px gap over a line-coloured list). Buttons
                    turn ghost in the level-2 list. */}
                <div className="border-t border-line-weakest p-cozy">
                    <ul
                        data-container="2"
                        aria-label={t('home__starters_label')}
                        className="flex flex-col @[44rem]:flex-row @[44rem]:gap-px @[44rem]:bg-line-weakest"
                    >
                        <Starter
                            icon={<CalendarCheckIcon />}
                            onClick={() => {
                                sendEvent('ai_chat_starter_question_selected', { question: 'visit_prep' });
                                const [before = '', after = ''] =
                                    t('home__starter_visit_prep').split('{{caret}}');
                                draftAskPlato({ before, after }, 'home_card');
                            }}
                        >
                            {t('home__starter_visit_prep_label')}
                        </Starter>
                        <Starter
                            icon={<TrendingDownIcon />}
                            onClick={() => {
                                sendEvent('ai_chat_starter_question_selected', {
                                    question: 'revenue_decline',
                                });
                                ask(t('home__starter_revenue_decline'));
                            }}
                        >
                            {t('home__starter_revenue_decline_label')}
                        </Starter>
                        <Starter
                            icon={<BarChart3Icon />}
                            onClick={() => {
                                sendEvent('ai_chat_starter_question_selected', {
                                    question: 'revenue_vs_last_year',
                                });
                                ask(t('home__starter_revenue_vs_last_year'));
                            }}
                        >
                            {t('home__starter_revenue_vs_last_year_label')}
                        </Starter>
                    </ul>
                </div>
            </div>
        </section>
    );
}

type StarterProps = {
    icon: ReactNode;
    onClick: () => void;
    children: ReactNode;
};

function Starter({ icon, onClick, children }: StarterProps): ReactElement {
    return (
        <li className="@[44rem]:flex-1 @[44rem]:bg-container @[44rem]:px-tight">
            <Button
                variant="secondary"
                onClick={onClick}
                className={cn(
                    'h-auto min-h-11 w-full justify-start py-cozy pl-0 text-left [&_svg]:text-content-weaker',
                    // Hover radius concentric with the card: 24px corner − 8px inset.
                    '@[44rem]:h-10 @[44rem]:min-h-0 @[44rem]:justify-center @[44rem]:rounded-2xl @[44rem]:py-0 @[44rem]:pl-default @[44rem]:text-center',
                )}
            >
                {/* Rows: the icon column lines the labels up with the placeholder. */}
                <span className="flex w-8 shrink-0 justify-center @[44rem]:w-auto">{icon}</span>
                <span className="text-body text-content">{children}</span>
            </Button>
        </li>
    );
}
